/**
 * @gearghost/page-meta: the words a page's head carries.
 *
 * WHY ONE PACKAGE: a listing or store page's title and description are read
 * in two places. The backend fills them into the first HTML the server
 * sends, because link previews (iMessage, Slack, Facebook) and most
 * crawlers never run our JavaScript. apps/web sets the browser tab when a
 * visitor moves between pages without a reload. Written twice, the tab and
 * the preview would drift; here they cannot.
 *
 * The wording is Tyler's (2026-09-29 and 2026-09-30, cards "WEB: home page
 * title and description in search results", "WEB: each public page has its
 * own title and description" and "WEB: public pages arrive as finished
 * HTML").
 */

/** The site's name, the last words of every page title. */
export const SITE_NAME = 'GearGhost';

/**
 * The address every canonical link and og:url names: production's, on
 * every environment, since staging asks search engines not to list it.
 */
export const SITE_ORIGIN = 'https://www.gearghost.co';

/**
 * The site-wide link-preview image: @gearghost/brand-assets' share card,
 * which the web build copies to the site root. index.html's og:image names
 * it, and a listing or store with no picture of its own shows it too.
 */
export const SHARE_CARD_URL = `${SITE_ORIGIN}/share-card.png`;

/**
 * The Sales Tax on Rentals guide's address. Named on its own because the
 * sitemap leaves it out while an admin has the Sales Tax switch off, when
 * web hides the guide (Tyler, 2026-10-21).
 */
export const SALES_TAX_GUIDE_PATH = '/guides/sales-tax';

/**
 * The public pages the sitemap lists besides every listing and store
 * (#1207 "WEB: sitemap of public pages, and missing pages marked missing"):
 * home, the guides, and the info pages, as apps/web's paths name them. Only
 * pages a visitor lands on, never an address that redirects. The backend
 * builds the sitemap from this, and a web test fails when a guide or one of
 * these addresses stops being a page the web serves. Never a page that asks
 * search engines not to list it: /open-source-licenses left on 2026-10-27,
 * a credits roll Google showed for searches about open source.
 */
export const SITEMAP_PAGE_PATHS = [
  '/',
  '/guides',
  '/guides/savings-tiers',
  SALES_TAX_GUIDE_PATH,
  '/guides/listings',
  '/damage-and-theft',
  '/how-it-works',
  '/aboutus',
  '/contactus',
  '/terms-of-service',
  '/privacy-policy',
  '/rules-and-guidelines',
  '/delete-account',
] as const;

/** Where GearGhost rents: the place a search for gear names. */
export const SERVICE_AREA = 'Los Angeles';

/**
 * The home page's title and description, and every page's until its own
 * load. apps/web/index.html repeats both, since HTML cannot import, and
 * its test fails when the two copies differ.
 */
export const SITE_TITLE = `GearGhost: Rent Camera Gear in ${SERVICE_AREA}`;
export const SITE_DESCRIPTION = `GearGhost is a rental marketplace for film and photo gear. Browse gear from local vendors, book your dates, and get shooting. Now serving ${SERVICE_AREA}.`;

/**
 * The longest description a page's head carries. Google cuts its snippet
 * near 160 characters and a link preview shows two or three lines, so text
 * past this is sent for nobody.
 */
export const DESCRIPTION_MAX_LENGTH = 160;

/** A page's title, and its description, or null when it has nothing to say. */
export interface PageMeta {
  title: string;
  description: string | null;
}

/** A page's title: its name, then the site's. */
export function pageTitle(name: string): string {
  return `${name} | ${SITE_NAME}`;
}

/** A listing page's words: its title, Google's line under it, and a link preview's line. */
export interface ListingPageMeta extends PageMeta {
  description: string;
  /**
   * Google's line without its price, for a pasted link's preview and the
   * Product's own description (Tyler, 2026-10-21: "Google gets the price and
   * a link preview does not"; a sent preview keeps the price from the day it
   * was sent, Google reads the page again).
   */
  previewDescription: string;
}

/** How many of a kit's pieces its line names before "and more". */
const GEAR_NAMED = 3;

/**
 * A listing page, worded for a search (Tyler, 2026-10-27): the title says
 * it is a rental in Los Angeles, and the line under it gives the price a
 * day, the store, where to pick up and what comes with it. Google prints a
 * product's price as a plain "$70.00", whatever unit the structured data
 * gives it, so the line says "a day" in words.
 *
 * `dailyPrice` is the price as running text writes it (money-utils'
 * formatCentsInText), null for a listing with no price a day. `pickupArea`
 * is the store's area as every public page shows it, city and state, null
 * for a store with no pickup row. `gear` is the kit as its page lists it,
 * the listing's own item marked: the line names the other pieces once each,
 * leaving out any named like the listing's own item (a store can hold two
 * items of one name).
 */
export function listingPageMeta(listing: {
  title: string;
  storeName: string;
  dailyPrice: string | null;
  pickupArea: { city: string | null; state: string | null } | null;
  gear: ReadonlyArray<{ title: string; isMainItem: boolean }>;
}): ListingPageMeta {
  const mainItems = new Set(listing.gear.filter((piece) => piece.isMainItem).map((piece) => piece.title.trim()));
  const pieces = [...new Set(listing.gear.filter((piece) => !piece.isMainItem).map((piece) => piece.title.trim()))].filter(
    (title) => !mainItems.has(title),
  );
  const city = listing.pickupArea?.city ?? null;
  const state = listing.pickupArea?.state ?? null;
  const pickup = city === null ? '' : `, pickup in ${state === null ? city : `${city}, ${state}`}`;
  const comesWith = pieces.length === 0 ? '' : ` Comes with ${gearList(pieces)}.`;
  const rest = `${listing.storeName}${pickup}.${comesWith}`;
  const previewDescription = oneLine(`From ${rest}`);
  return {
    title: pageTitle(`${listing.title} Rental in ${SERVICE_AREA}`),
    description: listing.dailyPrice === null ? previewDescription : oneLine(`${listing.dailyPrice} a day from ${rest}`),
    previewDescription,
  };
}

/** The pieces a line names: all of three or fewer, else the first three and more. */
function gearList(pieces: string[]): string {
  if (pieces.length > GEAR_NAMED) return `${pieces.slice(0, GEAR_NAMED).join(', ')} and more`;
  const last = pieces.at(-1);
  if (last === undefined) throw new Error('listingPageMeta: a gear list with no pieces');
  const before = pieces.slice(0, -1);
  return before.length === 0 ? last : `${before.join(', ')} and ${last}`;
}

/**
 * A line a builder writes, cut to fit. It always names something (a
 * listing's store, a category), so it is never empty; a null here would be
 * a bug, not a page with nothing to say.
 */
function oneLine(text: string): string {
  const line = pageDescription(text);
  if (line === null) throw new Error(`page-meta: an empty line from "${text}"`);
  return line;
}

/** A name as one segment of an address: lowercase words joined by hyphens, "&" as "and". */
function pathSegment(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** The city's own address, under which its category pages sit: /los-angeles. */
export const SERVICE_AREA_PATH = `/${pathSegment(SERVICE_AREA)}`;

/**
 * A category's page (Tyler, 2026-10-27): under the city, so a second city's
 * pages sit beside these later (/los-angeles/lenses). Built from the
 * category's name, which is unique and only the seed writes.
 */
export function categoryPath(categoryName: string): string {
  return `${SERVICE_AREA_PATH}/${pathSegment(categoryName)}`;
}

/**
 * The category whose page an address names, by the address's last part (the
 * route's `:category`), or undefined for one no category has. The web's page
 * and the server's head tags both find their category here, so the server
 * never words a page the browser answers not found, or the other way round.
 */
export function findCategoryByPathSegment<T extends { name: string }>(categories: T[], segment: string): T | undefined {
  const path = `${SERVICE_AREA_PATH}/${segment}`;
  return categories.find((category) => categoryPath(category.name) === path);
}

/** A category page's words: the heading over its listings, the title Google shows, and the line under both. */
export interface CategoryPageMeta extends PageMeta {
  heading: string;
  description: string;
}

/** A category page, worded for a search (Tyler, 2026-10-27). */
export function categoryPageMeta(category: { name: string }): CategoryPageMeta {
  const heading = `${category.name} for Rent in ${SERVICE_AREA}`;
  return {
    heading,
    title: pageTitle(heading),
    description: oneLine(
      `${category.name} for rent from local stores across ${SERVICE_AREA}. Browse the gear, book your dates and pick up near you.`,
    ),
  };
}

/** A store page. The store's `description` is plain text from a textarea. */
export function storePageMeta(store: { storeName: string; description: string | null }): PageMeta {
  return {
    title: pageTitle(`${store.storeName} gear rentals`),
    description: store.description === null ? null : pageDescription(store.description),
  };
}

/**
 * A page's description from plain text: one line, cut at the last whole
 * word that fits, with an ellipsis saying it goes on. Null when nothing is
 * left to say.
 */
export function pageDescription(text: string): string | null {
  const oneLine = text.replace(/\s+/g, ' ').trim();
  if (oneLine === '') return null;
  if (oneLine.length <= DESCRIPTION_MAX_LENGTH) return oneLine;
  const room = oneLine.slice(0, DESCRIPTION_MAX_LENGTH - 1);
  const lastSpace = room.lastIndexOf(' ');
  // One word longer than the limit has no space to cut at; it is cut inside.
  const kept = lastSpace > 0 ? room.slice(0, lastSpace) : room;
  return `${kept}…`;
}
